/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Panel } from '@devstart/react-bootstrap';
import React, { useEffect, useState } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import AdminSessionIcon from '@assets/icons/admin-session/admin-session';
import Faq from '@assets/icons/admin-session/faq';
import Medal from '@assets/icons/medal';
import DarkModeButton from '@components/DarkModeButton';
import { PermissionLevel } from '@utils/enums/permission-levels';
import { formatClassOption, isNacionalUser } from '@utils/general-functions';
import { isSourceAvailableCert } from '@utils/is-a-cert';
import {
  ClassGroupsTableProps,
  FetchClassGroupsProps
} from '@pages/admin/class-groups/types';
import { useUserAccess } from '@utils/hooks/use-user-access';
import { Link } from '../../helpers';
import { Themes } from '../../settings/theme';

import {
  apiLocation,
  courseSesiSenaiUrl
} from '../../../../../config/env.json';
import {
  getSupportWhatsappNumber,
  getSupportInfoText,
  buildWhatsappUrl
} from '../../../utils/sctec-config';

import {
  changeTourStatus,
  fetchAllClassGroups,
  hardGoTo as navigate,
  openSignoutModal
} from '../../../redux/actions';
import { User } from '../../../redux/prop-types';
import { allClassGroupsSelector, userSelector } from '../../../redux/selectors';
import { updateMyTheme } from '../../../redux/settings/actions';
import { AccessLevel } from '../../../utils/enums/access-levels';

import AccountCircle from '../../../assets/icons/account-circle';
import ChevronDown2 from '../../../assets/icons/chevron-down-2';
import ChevronUp2 from '../../../assets/icons/chevron-up-2';
import Help2 from '../../../assets/icons/help-2';
import House from '../../../assets/icons/house';
import LockOpen from '../../../assets/icons/lock-open';
import Power from '../../../assets/icons/power';
import WhatsApp from '../../../assets/icons/whatsapp';

import './nav-links.css';

export interface NavLinksProps {
  fetchState?: { pending: boolean };
  user: User;
  onHide: () => void;
  fetchAllClassGroups: (props: FetchClassGroupsProps) => void;
  allClassGroups: ClassGroupsTableProps;
}

export type LinkMapProps = {
  path?: string;
  title: string;
  icon?: JSX.Element;
  adminOnly?: boolean;
  subitems?: LinkMapProps[];
  hideForNacionalUsers?: boolean;
  onClick?: () => void;
  key?: string;
  underline?: boolean;
  allowedRoles?: PermissionLevel[];
};

export type SubitemSectionProps = {
  key: string;
  title: string;
  icon?: JSX.Element;
  subitems: LinkMapProps[];
};

const mapStateToProps = createSelector(
  userSelector,
  allClassGroupsSelector,
  (user: User, allClassGroups: ClassGroupsTableProps) => ({
    user,
    allClassGroups
  })
);

const mapDispatchToProps = {
  navigate,
  toggleNightMode: (theme: Themes) => updateMyTheme({ theme }),
  openSignoutModal,
  changeTourStatus,
  fetchAllClassGroups
};

const SubitemSection = ({
  key,
  title,
  icon,
  subitems
}: SubitemSectionProps) => {
  const [toggle, setToggle] = useState(false);

  return (
    <Panel eventKey='1' bsClass='custom-accordion-panel' expanded={toggle}>
      <Panel.Title
        key={key}
        toggle
        className='nav-item'
        onClick={() => setToggle(prev => !prev)}
      >
        {icon}
        <span className='nav-link'>{title}</span>
        {toggle ? (
          <ChevronUp2 className='accordion-chevron' />
        ) : (
          <ChevronDown2 className='accordion-chevron' />
        )}
      </Panel.Title>

      <Panel.Body collapsible>
        {subitems?.map(subitem => (
          <Link
            key={subitem.title}
            className='nav-item'
            to={subitem.path ?? '#'}
            target='_blank'
            onClick={subitem.onClick}
          >
            {subitem.icon}
            <span className='nav-link'>{subitem.title}</span>
          </Link>
        ))}
      </Panel.Body>
    </Panel>
  );
};

function NavLinks({
  fetchState,
  onHide,
  user,
  fetchAllClassGroups,
  allClassGroups
}: NavLinksProps) {
  const { t } = useTranslation();
  const [sourceName, setSourceName] = useState<string>('');

  const pending = fetchState?.pending;

  const { isSCTEC } = useUserAccess(user);
  const number = getSupportWhatsappNumber(isSCTEC);
  const infoText = getSupportInfoText(isSCTEC, 'error');
  const whatsappURL = buildWhatsappUrl(number, infoText);
  const allowedRoles = [
    PermissionLevel.SUPPORT,
    PermissionLevel.ADMIN,
    PermissionLevel.SUPER
  ];

  const userRole = (userType: PermissionLevel) => {
    switch (userType) {
      case PermissionLevel.SUPPORT:
        return 'Suporte';
      case PermissionLevel.ADMIN:
        return 'Adminstrador';
      case PermissionLevel.SUPER:
        return 'Super Adminstrador';
      default:
        return '';
    }
  };

  const linkMap: LinkMapProps[] = [
    {
      path: '/learn',
      title: t('buttons.start'),
      icon: <House className='fill-icon' />,
      adminOnly: false
    },
    {
      path: '/settings',
      title: t('buttons.profile'),
      icon: <AccountCircle className='fill-icon' />,
      adminOnly: false
    },
    {
      path: '/certificate-of-participation',
      title: isSourceAvailableCert(user.source)
        ? 'Certificados'
        : 'Atestados de participação',
      icon: <Medal />,
      adminOnly: false,
      hideForNacionalUsers: true
    },
    {
      path: '/faq',
      title: t('buttons.faq'),
      icon: <Faq />,
      adminOnly: false
    },
    {
      path: '/admin',
      title: t('buttons.admin-users'),
      icon: <AdminSessionIcon className='fill-icon' />,
      adminOnly: true
    },
    {
      title: t('buttons.help'),
      icon: <Help2 className='fill-icon fill-icon-help' />,
      subitems: [
        {
          path: String(whatsappURL),
          title: t('buttons.report-an-error'),
          icon: <WhatsApp className='fill-icon' />,
          onClick: () => {
            window.dataLayer.push({
              event: 'gtm.click',
              'gtm.elementClasses': 'help-link-whatsApp'
            });
            onHide();
          }
        }
      ]
    }
  ];

  useEffect(() => {
    if (!user.source) return;

    fetchAllClassGroups({
      order: 'ASC',
      sort: 'name',
      status: [],
      search: user.source,
      pageSize: 1,
      pageNumber: 1
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.source]);

  useEffect(() => {
    if (user.source && allClassGroups?.classGroupsSummary) {
      const formattedName = formatClassOption(
        user.source,
        allClassGroups.classGroupsSummary
      );
      setSourceName(formattedName);
    }
  }, [user.source, allClassGroups]);

  return pending ? (
    <div className='nav-skeleton' />
  ) : (
    <div className='nav-list'>
      <div className='logged-user'>
        <span className='user-name'>{user.name}</span>
        <span className='user-email'>{user.email}</span>
        <span className='user-email'>
          <strong>{userRole(user.type as PermissionLevel)}</strong>
        </span>
        <span className='user-email'>
          <strong>{sourceName || user.source}</strong>
        </span>
      </div>

      {user.access !== AccessLevel.COMPLETE && !isNacionalUser(user.access) && (
        <Link
          className='nav-item unlock-content'
          key='learn'
          to={`${courseSesiSenaiUrl}/busca?titulo=devstart`}
          onClick={onHide}
        >
          <LockOpen className='fill-icon' />
          <span className='nav-link'>{t('buttons.unlock-content')}</span>
        </Link>
      )}

      <div className='items-section'>
        {linkMap.map(
          ({
            path,
            title,
            icon,
            adminOnly,
            subitems,
            hideForNacionalUsers
          }) => {
            if (
              (hideForNacionalUsers &&
                (isNacionalUser(user.access) || isSCTEC)) ||
              (adminOnly && !allowedRoles.includes(user.type))
            ) {
              return <></>;
            }

            if (subitems && subitems.length > 0) {
              return (
                <SubitemSection
                  key={title}
                  title={title}
                  icon={icon}
                  subitems={subitems}
                />
              );
            }

            return (
              <Link
                key={title}
                className='nav-item'
                to={path ?? '#'}
                onClick={onHide}
              >
                {icon}
                <span className='nav-link'>{title}</span>
              </Link>
            );
          }
        )}
      </div>

      <DarkModeButton />

      <a
        key='sign-out'
        className='nav-item sign-out'
        href={`${apiLocation}/signout`}
        onClick={onHide}
      >
        <Power className='fill-icon' />
        <span className='nav-link'>{t('buttons.sign-out')}</span>
      </a>
    </div>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(NavLinks));
